import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { sleepAPI, trialAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { LogOut, Moon, Plus, BarChart3, Calendar, Clock, TrendingUp, Layers, User } from "lucide-react";
import SleepDiaryForm from "../components/SleepDiaryForm";
import SleepHistory from "../components/SleepHistory";
import SleepCharts from "../components/SleepCharts";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_16338d16-af9f-4ec6-a8f3-cd6da42efe59/artifacts/7xtaj2mx_WhatsApp%20Image%202026-03-02%20at%203.01.19%20PM%20%281%29.jpeg";

const UserDashboard = () => {
  const { user, logout } = useAuth();
  const [entries, setEntries] = useState([]);
  const [trials, setTrials] = useState([]);
  const [activeTrial, setActiveTrial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [entriesRes, trialsRes, activeTrialRes] = await Promise.all([
        sleepAPI.getMyEntries(),
        trialAPI.getTrials(),
        trialAPI.getActiveTrial()
      ]);
      setEntries(entriesRes.data);
      setTrials(trialsRes.data);
      setActiveTrial(activeTrialRes.data);
    } catch (error) {
      // If trials don't exist yet, just load entries
      try {
        const entriesRes = await sleepAPI.getMyEntries();
        setEntries(entriesRes.data);
      } catch {
        toast.error("Failed to load sleep entries");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEntrySubmitted = (newEntry) => {
    setEntries([newEntry, ...entries]);
    setShowForm(false);
    loadData(); // Reload to update trial info
    toast.success("Sleep entry recorded successfully!");
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await sleepAPI.deleteEntry(entryId);
      setEntries(entries.filter(e => e.id !== entryId));
      toast.success("Entry deleted");
    } catch (error) {
      toast.error("Failed to delete entry");
    }
  };

  // Calculate overview metrics
  const totalEntries = entries.length;
  const avgEfficiency = entries.length > 0 
    ? (entries.reduce((sum, e) => sum + e.sleep_efficiency, 0) / entries.length).toFixed(1)
    : 0;
  const goodSleepCount = entries.filter(e => e.sleep_classification === "Good").length;
  const latestEntry = entries[0];

  const metrics = [
    {
      title: "Days Tracked",
      value: totalEntries,
      target: 14,
      icon: Calendar,
      color: "text-primary",
      bgColor: "bg-primary/10"
    },
    {
      title: "Avg. Efficiency",
      value: `${avgEfficiency}%`,
      icon: TrendingUp,
      color: avgEfficiency >= 85 ? "text-green-600" : avgEfficiency >= 70 ? "text-amber-600" : "text-red-600",
      bgColor: avgEfficiency >= 85 ? "bg-green-100" : avgEfficiency >= 70 ? "bg-amber-100" : "bg-red-100"
    },
    {
      title: "Good Sleep Days",
      value: goodSleepCount,
      icon: Moon,
      color: "text-green-600",
      bgColor: "bg-green-100"
    },
    {
      title: "Latest Quality",
      value: latestEntry?.quality || "N/A",
      icon: Clock,
      color: "text-slate-600",
      bgColor: "bg-slate-100"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="user-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src={LOGO_URL} alt="SleepMed" className="h-10 w-auto" />
              <span className="font-heading font-bold text-xl text-slate-900">SleepMediary</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600 hidden sm:block">
                Welcome, <span className="font-medium text-slate-900">{user?.name}</span>
              </span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={logout}
                className="text-slate-600 hover:text-red-600"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Patient Info Card */}
        {user?.patient_id && (
          <Card className="mb-6 border-0 shadow-sm bg-white">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Patient ID</p>
                    <p className="font-mono font-bold text-lg text-primary">{user.patient_id}</p>
                  </div>
                </div>
                {activeTrial && (
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                      <Layers className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Current Trial</p>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900">{activeTrial.name}</p>
                        <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                      </div>
                      <p className="text-xs text-slate-500">Started: {activeTrial.start_date}</p>
                    </div>
                  </div>
                )}
                {trials.length > 1 && (
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Total Trials</p>
                    <p className="font-bold text-2xl text-slate-900">{trials.length}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Two-Week Sleep Diary</h1>
            <p className="text-slate-600 mt-1">
              Fill one form every day for 14 days to track your sleep patterns.
            </p>
          </div>
          <Button 
            onClick={() => setShowForm(true)}
            className="btn-primary"
            data-testid="new-entry-btn"
          >
            <Plus className="h-5 w-5 mr-2" />
            New Entry
          </Button>
        </div>

        {/* Progress Bar */}
        <Card className="mb-8 border-0 shadow-sm bg-gradient-to-r from-primary/5 to-primary/10">
          <CardContent className="py-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-700">Diary Progress</span>
              <span className="text-sm text-slate-600">{totalEntries}/14 days</span>
            </div>
            <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${Math.min((totalEntries / 14) * 100, 100)}%` }}
              ></div>
            </div>
            {totalEntries >= 14 && (
              <p className="text-sm text-green-600 mt-2 font-medium">
                Congratulations! You've completed your 14-day sleep diary.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {metrics.map((metric, index) => (
            <Card 
              key={metric.title}
              className="card-metric animate-fadeIn"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className={`h-10 w-10 rounded-lg ${metric.bgColor} flex items-center justify-center`}>
                <metric.icon className={`h-5 w-5 ${metric.color}`} />
              </div>
              <div>
                <p className="text-sm text-slate-500">{metric.title}</p>
                <p className={`text-2xl font-bold ${metric.color}`}>{metric.value}</p>
                {metric.target && (
                  <p className="text-xs text-slate-400">of {metric.target} days</p>
                )}
              </div>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <BarChart3 className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Calendar className="h-4 w-4 mr-2" />
              History
            </TabsTrigger>
            <TabsTrigger value="trends" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <TrendingUp className="h-4 w-4 mr-2" />
              Trends
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            {loading ? (
              <div className="grid gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-24 bg-white rounded-xl skeleton"></div>
                ))}
              </div>
            ) : entries.length === 0 ? (
              <Card className="card-base text-center py-12">
                <Moon className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No entries yet</h3>
                <p className="text-slate-500 mb-6">
                  Start tracking your sleep by adding your first entry.
                </p>
                <Button onClick={() => setShowForm(true)} className="btn-primary">
                  <Plus className="h-5 w-5 mr-2" />
                  Add First Entry
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Recent Entries</h3>
                <SleepHistory 
                  entries={entries.slice(0, 5)} 
                  onDelete={handleDeleteEntry}
                  showAll={false}
                />
              </div>
            )}
          </TabsContent>

          <TabsContent value="history">
            <SleepHistory 
              entries={entries} 
              onDelete={handleDeleteEntry}
              showAll={true}
            />
          </TabsContent>

          <TabsContent value="trends">
            <SleepCharts entries={entries} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Sleep Diary Form Modal */}
      {showForm && (
        <SleepDiaryForm 
          onClose={() => setShowForm(false)}
          onSubmit={handleEntrySubmitted}
        />
      )}
    </div>
  );
};

export default UserDashboard;
